/**
 * Settings → Sold Jobs & Load Calc (JOB-SOLD-01, Seth 2026-09-28; everything admin-editable).
 *   · Photos for the Load Calc — the phone's photo checklist (org_setting sale_photo_slots: [{label, required, hint}])
 *   · Site Facts — what the tech is asked (sale_site_facts: [{key, label, kind, options, placeholder, unit, required}])
 *   · Load Calc — the program's name + link, whether confirming needs one, and the sizing limits (load_calc_rules)
 */
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import {
  DEFAULT_LOAD_CALC_RULES, DEFAULT_SALE_PHOTOS, DEFAULT_SITE_FACTS, factKey, useLoadCalcRules, useSalePhotoSlots, useSiteFacts,
  type FactKind, type LoadCalcRules, type SalePhotoSlot, type SiteFact,
} from "@/features/jobs/saleHandoff";

const inp = "h-9 w-full min-w-0 rounded-lg border border-hairline px-3 text-sm focus:border-brand-400 focus:outline-none disabled:bg-slate-50";
const iconBtn = "rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700";

function moveIn<T>(list: T[], i: number, d: -1 | 1): T[] {
  const n = [...list];
  const j = i + d;
  if (j < 0 || j >= n.length) return list;
  [n[i], n[j]] = [n[j], n[i]];
  return n;
}

export function SoldHandoffSettings() {
  return (
    <div className="space-y-4">
      <PhotoSlotsCard />
      <SiteFactsCard />
      <LoadCalcCard />
    </div>
  );
}

function PhotoSlotsCard() {
  const canEdit = useCan("settings.manage");
  const save = useSaveOrgSetting();
  const q = useSalePhotoSlots();
  const [list, setList] = useState<SalePhotoSlot[]>([]);
  useEffect(() => { if (q.data) setList(q.data); }, [q.data]);
  const dirty = JSON.stringify(list) !== JSON.stringify(q.data ?? []);
  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        Photos for the Load Calc
        <InfoTip label="Where this list shows">The tech&apos;s install packet on the phone asks for these, one tap each. The job&apos;s Sold tab shows them in this order, with any required one that&apos;s missing. Renaming a photo here doesn&apos;t move photos already taken.</InfoTip>
      </h3>
      <ul className="mt-3 space-y-2">
        {list.map((p, i) => (
          <li key={i} className="grid items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto_auto]">
            <input value={p.label} disabled={!canEdit} placeholder="Photo" aria-label="Photo"
              onChange={(e) => setList((l) => l.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} className={inp} />
            <input value={p.hint ?? ""} disabled={!canEdit} placeholder="A tip for the tech (optional)" aria-label="Tip"
              onChange={(e) => setList((l) => l.map((x, k) => (k === i ? { ...x, hint: e.target.value } : x)))} className={inp} />
            <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600">
              <input type="checkbox" disabled={!canEdit} checked={p.required}
                onChange={(e) => setList((l) => l.map((x, k) => (k === i ? { ...x, required: e.target.checked } : x)))} className="h-4 w-4 accent-brand-700" />
              Required
            </label>
            {canEdit && (
              <span className="flex shrink-0 items-center">
                <button type="button" aria-label="Move up" onClick={() => setList((l) => moveIn(l, i, -1))} className={iconBtn}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" aria-label="Move down" onClick={() => setList((l) => moveIn(l, i, 1))} className={iconBtn}><ArrowDown className="h-4 w-4" /></button>
                <button type="button" aria-label="Remove" tabIndex={-1} onClick={() => setList((l) => l.filter((_, k) => k !== i))} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setList((l) => [...l, { label: "", required: false }])}><Plus className="h-3.5 w-3.5" /> Add a Photo</Button>
          <Button size="sm" variant="ghost" onClick={() => setList(DEFAULT_SALE_PHOTOS)}><RotateCcw className="h-3.5 w-3.5" /> Starting List</Button>
          {dirty && (
            <Button size="sm" disabled={save.isPending}
              onClick={() => save.mutate({ key: "sale_photo_slots", value: list.filter((p) => p.label.trim()).map((p) => ({ label: p.label.trim(), required: p.required, ...(p.hint?.trim() ? { hint: p.hint.trim() } : {}) })) })}>
              Save the List
            </Button>
          )}
        </div>
      )}
      {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this.</p>}
    </Card>
  );
}

const KIND_LABEL: Record<FactKind, string> = { choice: "Choices", number: "Number", text: "Text" };

function SiteFactsCard() {
  const canEdit = useCan("settings.manage");
  const save = useSaveOrgSetting();
  const q = useSiteFacts();
  const [list, setList] = useState<SiteFact[]>([]);
  useEffect(() => { if (q.data) setList(q.data); }, [q.data]);
  const dirty = JSON.stringify(list) !== JSON.stringify(q.data ?? []);
  const upd = (i: number, patch: Partial<SiteFact>) => setList((l) => l.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const clean = () => {
    const used = new Set<string>();
    return list.filter((f) => f.label.trim()).map((f) => {
      let key = f.key?.trim() || factKey(f.label);
      while (used.has(key)) key = `${key}_2`;
      used.add(key);
      const out: SiteFact = { key, label: f.label.trim(), kind: f.kind };
      if (f.kind === "choice") out.options = (f.options ?? []).map((o) => o.trim()).filter(Boolean);
      if (f.placeholder?.trim()) out.placeholder = f.placeholder.trim();
      if (f.unit?.trim()) out.unit = f.unit.trim();
      if (f.required) out.required = true;
      return out;
    });
  };
  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        Site Facts
        <InfoTip label="Where these show">The tech answers these in the install packet — choices are one tap. The Sold tab lists them beside the photos for whoever does the load calc. A fact&apos;s saved answers stay with it when you rename it.</InfoTip>
      </h3>
      <ul className="mt-3 space-y-3">
        {list.map((f, i) => (
          <li key={i} className="rounded-lg border border-hairline p-3">
            <div className="grid items-center gap-2 sm:grid-cols-[minmax(0,1.4fr)_9rem_minmax(0,1fr)_auto_auto]">
              <input value={f.label} disabled={!canEdit} placeholder="What to ask" aria-label="Fact" onChange={(e) => upd(i, { label: e.target.value })} className={inp} />
              <select value={f.kind} disabled={!canEdit} aria-label="Answer type" onChange={(e) => upd(i, { kind: e.target.value as FactKind })} className={inp}>
                {(Object.keys(KIND_LABEL) as FactKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
              <input value={f.kind === "choice" ? "" : f.placeholder ?? ""} disabled={!canEdit || f.kind === "choice"} placeholder={f.kind === "choice" ? "—" : "Example answer"} aria-label="Example"
                onChange={(e) => upd(i, { placeholder: e.target.value })} className={inp} />
              <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600">
                <input type="checkbox" disabled={!canEdit} checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} className="h-4 w-4 accent-brand-700" />
                Required
              </label>
              {canEdit && (
                <span className="flex shrink-0 items-center">
                  <button type="button" aria-label="Move up" onClick={() => setList((l) => moveIn(l, i, -1))} className={iconBtn}><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label="Move down" onClick={() => setList((l) => moveIn(l, i, 1))} className={iconBtn}><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label="Remove" tabIndex={-1} onClick={() => setList((l) => l.filter((_, k) => k !== i))} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </span>
              )}
            </div>
            {f.kind === "choice" && (
              <input value={(f.options ?? []).join(", ")} disabled={!canEdit} placeholder="Choices, separated by commas" aria-label="Choices"
                onChange={(e) => upd(i, { options: e.target.value.split(",").map((o) => o.trimStart()) })} className={`${inp} mt-2`} />
            )}
            {f.kind === "number" && (
              <input value={f.unit ?? ""} disabled={!canEdit} placeholder="Unit (sq ft, ft…)" aria-label="Unit" onChange={(e) => upd(i, { unit: e.target.value })} className={`${inp} mt-2 sm:w-48`} />
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setList((l) => [...l, { key: "", label: "", kind: "choice", options: [] }])}><Plus className="h-3.5 w-3.5" /> Add a Fact</Button>
          <Button size="sm" variant="ghost" onClick={() => setList(DEFAULT_SITE_FACTS)}><RotateCcw className="h-3.5 w-3.5" /> Starting List</Button>
          {dirty && <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ key: "sale_site_facts", value: clean() })}>Save the List</Button>}
        </div>
      )}
      {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this.</p>}
    </Card>
  );
}

const LIMITS: { key: keyof LoadCalcRules; label: string }[] = [
  { key: "ac_min", label: "Air conditioner — at least" }, { key: "ac_max", label: "Air conditioner — at most" },
  { key: "hp_min", label: "Heat pump — at least" }, { key: "hp_max", label: "Heat pump — at most" },
  { key: "furnace_min", label: "Furnace output — at least" }, { key: "furnace_max", label: "Furnace output — at most" },
];

function LoadCalcCard() {
  const canEdit = useCan("settings.manage");
  const save = useSaveOrgSetting();
  const { rules, data } = useLoadCalcRules();
  const [d, setD] = useState<LoadCalcRules>(DEFAULT_LOAD_CALC_RULES);
  useEffect(() => { setD(rules); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(d) !== JSON.stringify(rules);
  const bad = LIMITS.some((l) => !Number.isFinite(Number(d[l.key])) || Number(d[l.key]) <= 0)
    || d.ac_min > d.ac_max || d.hp_min > d.hp_max || d.furnace_min > d.furnace_max;
  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        Load Calc
        <InfoTip label="How the Equipment Check uses this">The Equipment Check on a job&apos;s Sold tab compares each system to the load calc: cooling as a percent of the heat gain, a furnace&apos;s output as a percent of the heat loss. The starting limits are ACCA Manual S. It checks the nominal size (or the AHRI rating when there is one), so the load calc program&apos;s own equipment selection is still the final word.</InfoTip>
      </h3>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">The program you use</span>
          <input value={d.tool_label} disabled={!canEdit} onChange={(e) => setD({ ...d, tool_label: e.target.value })} placeholder="CoolCalc" className={`${inp} mt-1 h-10`} />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">Its web address (the Sold tab links to it)</span>
          <input value={d.tool_url} disabled={!canEdit} onChange={(e) => setD({ ...d, tool_url: e.target.value })} placeholder="https://…" className={`${inp} mt-1 h-10`} />
        </label>
      </div>
      <label className="mt-4 flex items-center gap-3">
        <input type="checkbox" disabled={!canEdit} checked={d.required} onChange={(e) => setD({ ...d, required: e.target.checked })} className="h-4 w-4 accent-brand-700" />
        <span className="text-sm text-slate-700">Confirming the equipment needs a load calc (or Not Needed and why)</span>
      </label>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {LIMITS.map((l) => (
          <label key={l.key} className="block">
            <span className="text-xs font-semibold text-slate-500">{l.label}</span>
            <span className="relative mt-1 block">
              <input type="number" min={1} value={String(d[l.key])} disabled={!canEdit}
                onChange={(e) => setD({ ...d, [l.key]: Number(e.target.value) } as LoadCalcRules)}
                className={`${inp} h-10 pr-8 text-right tabular-nums`} />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
            </span>
          </label>
        ))}
      </div>
      {canEdit && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setD({ ...d, ...DEFAULT_LOAD_CALC_RULES, tool_label: d.tool_label, tool_url: d.tool_url })}><RotateCcw className="h-3.5 w-3.5" /> Manual S Limits</Button>
          {dirty && <Button size="sm" disabled={save.isPending || bad} onClick={() => save.mutate({ key: "load_calc_rules", value: { ...d, tool_label: d.tool_label.trim(), tool_url: d.tool_url.trim() } })}>Save</Button>}
          {bad && <span className="text-xs font-medium text-rose-600">Each “at least” has to be below its “at most”.</span>}
        </div>
      )}
      {save.isError && <p className="mt-2 text-xs font-medium text-rose-600">Couldn&apos;t save — only an admin can change this.</p>}
    </Card>
  );
}
